import { getQueryKey_escape_hatch as getQueryKeySearchProductsFromBrand } from "@faire/web-api/api/v2/search/products/from-brand/post-hooks";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useLocalization } from "@faire/web/common/localization/useLocalization";
import { PerformanceMarkStartTime } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import React from "react";
import { useHistory } from "react-router-dom";

import { getBrandAboutSubheader } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(defaultHeader)/brand/[brandToken]/(rsc)/rsc/_sections/Info/Story/getBrandAboutSubheader";
import { useLoggedOutProductFilters } from "@faire/retailer/components/LoggedOutBrand/brand-products/useLoggedOutProductFilters";
import { BrandProfileSummary } from "@faire/retailer/components/LoggedOutBrand/BrandProfileSummary";
import { useBrandFilterSectionsData } from "@faire/retailer/components/LoggedOutBrand/hooks/useBrandFilterSectionsData";
import { useBrandLeanProductTilesData } from "@faire/retailer/components/LoggedOutBrand/hooks/useBrandLeanProductTilesData";

export const BrandProfileSummaryBase: React.FC<{
  brandToken: string;
  brand: IBrand;
  pageJsExecutedMark: PerformanceMarkStartTime | undefined;
}> = ({ brand, brandToken, pageJsExecutedMark }) => {
  const { strictLocalize } = useStrictLocalization();
  const { localeKey } = useLocalization();
  const { push } = useHistory();
  const { selectedCategoryFilterKey } = useLoggedOutProductFilters(push);

  const {
    searchProductsFromBrandRequest,
    brandLeanProductTilesData,
    brandLeanProductTilesIsPending,
    brandLeanProductTilesDataUpdatedAt,
  } = useBrandLeanProductTilesData({
    brandToken: brandToken,
    selectedCategoryFilterKey,
    refP: undefined,
  });

  const { filterSections, categoryFilterOptions } = useBrandFilterSectionsData({
    brandToken: brandToken,
  });

  const brandAboutSubheader = getBrandAboutSubheader(
    brand,
    filterSections ?? [],
    localeKey,
    strictLocalize
  );

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-faire-direct-landing",
    pageLoadKey: JSON.stringify(
      getQueryKeySearchProductsFromBrand(searchProductsFromBrandRequest)
    ),
    dataUpdatedAt: brandLeanProductTilesDataUpdatedAt,
    numComponents: 2,
    isPageLoading: brandLeanProductTilesIsPending,
    pageJsExecutedMark: pageJsExecutedMark,
    enableTrackPerformanceBreakdown: "internal-tracking",
    // This is needed because of the sign-up query param that immediately follows on landing.
    enablePageUpdateFix: true,
  });

  return (
    <BrandProfileSummary
      brand={brand}
      productTiles={brandLeanProductTilesData?.product_tiles}
      productTilesIsLoading={brandLeanProductTilesIsPending}
      productsRequestId={brandLeanProductTilesData?.request_id}
      totalProductsCount={
        brandLeanProductTilesData?.pagination_data?.total_results
      }
      showVerticalImagery={
        !!brandLeanProductTilesData?.should_show_vertical_image
      }
      brandAboutSubheader={brandAboutSubheader}
      categoryFilterOptions={categoryFilterOptions}
      isFaireDirectLandingPage
      loadTimingRef={loadTimingRef}
      numberOfProductsToShow={FAIRE_DIRECT_LANDING_PAGE_NUM_DISPLAY_PRODUCTS}
    />
  );
};

const FAIRE_DIRECT_LANDING_PAGE_NUM_DISPLAY_PRODUCTS = 4;
